import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { fetchCurrentUser } from '../../api/me';
import { getApiToken } from '../../api/authToken';
import { isAppEmailVerificationRestrictedUser } from './appEmailVerificationRestriction';

const EmailVerificationRestrictionContext = createContext(false);

const POLL_INTERVAL_MS = 10000;

export function EmailVerificationRestrictionProvider({ children }) {
  const { user, updateUser } = useAuth();
  const shouldRestrict = useMemo(
    () => isAppEmailVerificationRestrictedUser(user),
    [user],
  );
  const [restricted, setRestricted] = useState(shouldRestrict);

  useEffect(() => {
    setRestricted(shouldRestrict);

    if (!shouldRestrict || !user?.uid) {
      return undefined;
    }

    const pollVerificationStatus = async () => {
      try {
        const token = await getApiToken();
        const result = await fetchCurrentUser(token);
        if (result?.user?.emailVerified === true) {
          updateUser((current) => (
            current ? { ...current, emailVerified: true } : current
          ));
          setRestricted(false);
        }
      } catch (error) {
        console.error('Error checking email verification status:', error);
      }
    };

    void pollVerificationStatus();
    const intervalId = setInterval(pollVerificationStatus, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [shouldRestrict, updateUser, user?.uid]);

  return (
    <EmailVerificationRestrictionContext.Provider value={restricted}>
      {children}
    </EmailVerificationRestrictionContext.Provider>
  );
}

export function useAppEmailVerificationRestricted() {
  return useContext(EmailVerificationRestrictionContext);
}
